import { api, type LearningResponse } from '../lib/api';
import { useResource } from '../lib/useResource';
import { formatNumber } from '../lib/dataState';
import { formatInstant } from '../lib/format';

/**
 * Aprendizado e recomendações.
 *
 * A tela existe para tornar visível a distinção que o agente de Learning tem
 * incentivo a borrar: **a força da conclusão**. Uma hipótese sobre três casos e
 * uma conclusão sobre quarenta parecem iguais quando só o texto aparece — e é
 * assim que uma observação vira "regra" na cabeça de quem lê.
 *
 * Por isso a força vem antes do texto, e o tamanho da amostra aparece sempre.
 */

const STRENGTH_LABEL: Record<string, string> = {
  weak_hypothesis: 'Hipótese fraca',
  supported: 'Apoiada',
  strong: 'Forte',
  confounded: 'Confundida',
  inconclusive: 'Inconclusiva',
};

const STRENGTH_CLASS: Record<string, string> = {
  weak_hypothesis: 'border-l-4 border-l-warn',
  supported: 'border-l-4 border-l-accent',
  strong: 'border-l-4 border-l-ok',
  confounded: 'border-l-4 border-l-danger bg-danger-surface/50',
  inconclusive: 'border-l-4 border-l-line-strong',
};

const CHANGE_STATE_LABEL: Record<string, string> = {
  monitoring: 'Em monitoramento',
  sustained: 'Sustentada',
  rolled_back: 'Revertida',
};

/** Valor de parâmetro como texto legível, sem `[object Object]` na tela. */
function valorLegivel(v: unknown): string {
  if (v === null || v === undefined) return 'não definido';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

export function Learning() {
  const resource = useResource<LearningResponse>(() => api.learning(), []);

  if (resource.kind === 'loading') {
    return (
      <div className="text-sm" aria-busy="true">
        <h1 className="text-2xl font-bold">Aprendizado e recomendações</h1>
        <p className="mt-2 text-ink-muted">Carregando…</p>
      </div>
    );
  }

  if (resource.kind === 'error') {
    return (
      <div className="max-w-3xl space-y-4 text-sm">
        <h1 className="text-2xl font-bold">Aprendizado e recomendações</h1>
        <div className="mt-4 rounded border border-danger-border bg-danger-surface p-4">
          <p className="font-medium">Não foi possível carregar o aprendizado.</p>
          <p className="mt-1">{resource.message}</p>
          <p className="mt-2 text-xs text-ink-faint">
            Isto não significa que não há pedidos esperando sua decisão. Significa que não sabemos.
            Código: {resource.code}
          </p>
        </div>
      </div>
    );
  }

  const { data } = resource;

  return (
    <div className="space-y-4 text-sm">
      <header>
        <h1 className="text-2xl font-bold">Aprendizado e recomendações</h1>
        <p className="mt-1 text-ink-muted">
          Aprender não é aplicar. Só {formatNumber(data.auto_apply_allowlist.length)} parâmetro(s)
          entram sozinhos; o resto depende de decisão humana.
        </p>
      </header>

      {/* ---- Pedidos ao dono: o único item que espera decisão --------------- */}
      {data.owner_requests.length > 0 ? (
        <section className="mt-6" aria-labelledby="pedidos">
          <h2
            id="pedidos"
            className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
          >
            {formatNumber(data.owner_requests.length)} pedido(s) esperando sua decisão
          </h2>
          <ul className="mt-3 space-y-3">
            {data.owner_requests.map((o) => (
              <li
                key={o.request_id}
                className="rounded border border-warn-border bg-warn-surface/60 p-4"
              >
                <p className="font-medium">{o.target_parameter ?? o.request_type}</p>
                <p className="mt-1">
                  De <span className="font-mono text-xs">{valorLegivel(o.current_value)}</span> para{' '}
                  <span className="font-mono text-xs">{valorLegivel(o.requested_value)}</span>
                </p>
                <p className="mt-2">{o.rationale}</p>
                <p className="mt-2 text-xs text-ink-faint">
                  Pedido por {o.requested_by} em {formatInstant(o.created_at)}
                  {o.evidence_refs.length > 0
                    ? ` · evidência: ${o.evidence_refs.join(', ')}`
                    : ' · sem evidência anexada'}
                </p>
                <p className="mt-2 text-xs text-ink-muted">
                  O agente criou o pedido em vez de alterar o limite. A mudança só acontece por
                  decisão sua, registrada.
                </p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* ---- Sinais ------------------------------------------------------- */}
      <section className="mt-8" aria-labelledby="sinais">
        <h2 id="sinais" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
          Sinais de aprendizado ({formatNumber(data.signals.length)})
        </h2>
        {data.signals.length === 0 ? (
          <p className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
            Nenhum sinal registrado. Aprendizado depende de publicações medidas.
          </p>
        ) : (
          <ul className="mt-3 space-y-3">
            {data.signals.map((s) => (
              <li
                key={s.signal_id}
                className={`rounded-card border border-line bg-card p-4 shadow-card ${STRENGTH_CLASS[s.strength] ?? ''}`}
              >
                {/*
                  A força vem ANTES do texto, e a amostra aparece sempre. Ler a
                  observação primeiro e a força depois é como uma hipótese vira
                  regra na cabeça de quem lê.
                */}
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide">
                    {STRENGTH_LABEL[s.strength] ?? s.strength}
                  </span>
                  <span className="text-xs text-ink-muted">n = {formatNumber(s.sample_size)}</span>
                </div>
                <p className="mt-2">{s.observation}</p>
                {s.confounders.length > 0 ? (
                  <p className="mt-2 text-xs text-danger-ink">
                    Confundida com: {s.confounders.join(', ')}. O resultado não se atribui a nenhuma
                    delas: o próximo passo é isolar, não escolher a mais provável.
                  </p>
                ) : null}
                <p className="mt-2 text-xs text-ink-faint">
                  {s.evidence_refs.length > 0
                    ? `Evidência: ${s.evidence_refs.join(', ')}`
                    : 'Sem evidência anexada'}{' '}
                  · {formatInstant(s.created_at)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ---- Recomendações ------------------------------------------------ */}
      <section className="mt-8" aria-labelledby="recomendacoes">
        <h2
          id="recomendacoes"
          className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
        >
          Recomendações ({formatNumber(data.recommendations.length)})
        </h2>
        {data.recommendations.length === 0 ? (
          <p className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
            Nenhuma recomendação proposta.
          </p>
        ) : (
          <ul className="mt-3 space-y-3">
            {data.recommendations.map((r) => (
              <li
                key={r.recommendation_id}
                className="rounded-card border border-line bg-card p-4 shadow-card"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-medium">{r.target_parameter}</span>
                  <span className="text-xs text-ink-muted">{r.state}</span>
                </div>
                <p className="mt-1">
                  <span className="font-mono text-xs">{valorLegivel(r.current_value)}</span> →{' '}
                  <span className="font-mono text-xs">{valorLegivel(r.proposed_value)}</span>
                </p>
                <p className="mt-2">{r.rationale}</p>
                <p className="mt-2 text-xs">
                  {r.auto_applicable ? (
                    <span className="text-accent">
                      Na allowlist: pode ser aplicada automaticamente, e nasce reversível.
                    </span>
                  ) : (
                    <span className="text-ink-muted">
                      {r.auto_apply_blocked_reason ?? 'Não é auto-aplicável.'}
                    </span>
                  )}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ---- Mudanças aplicadas ------------------------------------------- */}
      <section className="mt-8" aria-labelledby="mudancas">
        <h2 id="mudancas" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
          Mudanças aplicadas ({formatNumber(data.changes.length)})
        </h2>
        {data.changes.length === 0 ? (
          <p className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
            Nenhum parâmetro foi alterado por aprendizado.
          </p>
        ) : (
          <ul className="mt-3 space-y-3">
            {data.changes.map((c) => (
              <li
                key={c.change_id}
                className={`rounded-card border border-line bg-card p-4 shadow-card ${
                  c.state === 'rolled_back' ? 'border-l-4 border-l-danger' : ''
                }`}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-medium">{c.parameter_key}</span>
                  <span className="text-xs text-ink-muted">
                    {CHANGE_STATE_LABEL[c.state] ?? c.state}
                  </span>
                </div>
                <p className="mt-1">
                  <span className="font-mono text-xs">{valorLegivel(c.previous_value)}</span> →{' '}
                  <span className="font-mono text-xs">{valorLegivel(c.new_value)}</span>
                </p>
                <p className="mt-2 text-xs text-ink-muted">
                  Vigiando {c.monitor_metric}
                  {c.monitor_baseline === null ? (
                    /*
                      Sem baseline não há contra o que comparar — e dizer isso é
                      mais honesto do que exibir a métrica como se ela estivesse
                      sendo avaliada.
                    */
                    <span className="text-warn-ink"> · sem baseline registrada</span>
                  ) : (
                    ` · baseline ${formatNumber(c.monitor_baseline)}`
                  )}{' '}
                  · janela até {formatInstant(c.monitor_window_end)}
                </p>
                {c.rollback_reason ? (
                  <p className="mt-2 text-xs text-danger-ink">{c.rollback_reason}</p>
                ) : null}
                <p className="mt-2 text-xs text-ink-faint">
                  Aplicada por {c.applied_by} em {formatInstant(c.applied_at)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
